import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './MultiSelectChip.module.css';

export interface MultiSelectOption {
  id: string;
  label: string;
  /** Color opcional para el dot junto a la opción (ej: color del EV). */
  color?: string;
}

interface Props {
  icon?: string;
  /** Texto en singular (ej: "planta"). Se usa en "Todas las plantas" / "N plantas". */
  singular: string;
  plural: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Texto a usar cuando hay 0 opciones disponibles. */
  emptyText?: string;
  /** Permite ocultar el chip si no hay opciones (jerarquía). */
  hideIfEmpty?: boolean;
  searchable?: boolean;
}

/**
 * Chip multi-select compacto con dropdown.
 *
 * - Si no hay selección → "Todas las {plural}" (chip "vacío", visualmente neutro)
 * - Si hay selección → "N {plural}" (chip activo) + botón ✕ para limpiar
 * - Click abre dropdown con checkboxes, buscador y "seleccionar todas / ninguna"
 */
export function MultiSelectChip({
  icon,
  singular,
  plural,
  options,
  selected,
  onChange,
  emptyText,
  hideIfEmpty = false,
  searchable = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search]);

  if (hideIfEmpty && options.length === 0 && selected.length === 0) return null;

  const isActive = selected.length > 0;
  const label =
    selected.length === 0
      ? `Todas las ${plural}`
      : selected.length === 1
        ? options.find((o) => o.id === selected[0])?.label || `1 ${singular}`
        : `${selected.length} ${plural}`;

  function toggle(id: string) {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.chip} ${isActive ? styles.chipActive : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        {icon && <span className={styles.chipIcon}>{icon}</span>}
        <span>{label}</span>
        {isActive ? (
          <span
            className={styles.chipCloseBtn}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            role="button"
            aria-label={`Quitar filtro de ${plural}`}
          >
            ✕
          </span>
        ) : (
          <span style={{ fontSize: 11, opacity: 0.55 }}>▾</span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown}>
          {searchable && options.length > 5 && (
            <div className={styles.dropdownHeader}>
              <input
                type="search"
                className={styles.dropdownSearch}
                placeholder={`Buscar ${singular}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {options.length > 0 && (
            <div className={styles.dropdownActions}>
              <button
                type="button"
                className={styles.dropdownActionBtn}
                onClick={() => onChange(options.map((o) => o.id))}
              >
                Seleccionar todas
              </button>
              <button
                type="button"
                className={styles.dropdownActionBtn}
                onClick={() => onChange([])}
                style={{ color: 'var(--text-soft)' }}
              >
                Limpiar
              </button>
            </div>
          )}

          <div className={styles.dropdownList}>
            {filtered.length === 0 && (
              <div className={styles.dropdownEmpty}>
                {options.length === 0
                  ? emptyText || `No hay ${plural} disponibles`
                  : `Sin resultados para "${search}"`}
              </div>
            )}
            {filtered.map((o) => {
              const checked = selected.includes(o.id);
              return (
                <label key={o.id} className={styles.dropdownItem}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(o.id)}
                  />
                  {o.color && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: o.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span className={styles.dropdownItemLabel}>{o.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
